import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { FiUser, FiGrid, FiBriefcase } from 'react-icons/fi';

const OrganizationBrand = ({ 
  size = 'md', 
  showName = true, 
  showLogo = true, 
  className = '',
  variant = 'default' // 'default', 'minimal', 'hero'
}) => {
  const { getOrganizationBranding, isOrganizationThemed } = useTheme();
  const { isEnterpriseUser } = useAuth();
  
  const branding = getOrganizationBranding();
  
  // Size configurations
  const sizeConfig = {
    sm: {
      logoSize: 'w-6 h-6',
      textSize: 'text-sm',
      iconSize: 'w-4 h-4',
    },
    md: {
      logoSize: 'w-8 h-8',
      textSize: 'text-base',
      iconSize: 'w-5 h-5',
    },
    lg: {
      logoSize: 'w-12 h-12',
      textSize: 'text-lg',
      iconSize: 'w-6 h-6',
    },
    xl: {
      logoSize: 'w-16 h-16',
      textSize: 'text-xl',
      iconSize: 'w-8 h-8',
    },
  };
  
  const config = sizeConfig[size] || sizeConfig.md;
  
  // Variant configurations
  const getVariantClasses = () => {
    // When organization themed, use CSS variables
    if (isOrganizationThemed) {
      return ''; // We'll use inline style instead
    }
    
    switch (variant) {
      case 'minimal':
        return 'text-gray-600';
      case 'hero':
        return 'text-gray-900 font-bold';
      default:
        return 'text-gray-800';
    }
  };

  const getVariantStyle = () => {
    if (isOrganizationThemed) {
      return { color: 'var(--sidebar-text, var(--org-sidebar-text, #FFFFFF))' };
    }
    return undefined;
  };

  const renderLogo = () => {
    if (!showLogo) return null;
    
    if (branding.logo) {
      return (
        <img
          src={branding.logo}
          alt={`${branding.name} logo`}
          className={`${config.logoSize} object-contain rounded-xl`}
        />
      );
    }
    
    // Fallback icons based on user type
    const IconComponent = isEnterpriseUser() ? FiBriefcase : FiUser;
    
    return (
      <div 
        className={`${config.logoSize} rounded-xl flex items-center justify-center`}
        style={{ 
          backgroundColor: isOrganizationThemed ? 'var(--color-primary)' : '#3B82F6',
          color: 'white'
        }}
      >
        <IconComponent className={config.iconSize} />
      </div>
    );
  };

  const renderName = () => {
    if (!showName) return null;
    
    return (
      <span 
        className={`${config.textSize} ${getVariantClasses()} font-medium break-words line-clamp-2`}
        style={getVariantStyle()}
        title={branding.name}
      >
        {branding.name}
      </span>
    );
  };

  const renderSubdomain = () => {
    if (variant !== 'hero' || !branding.subdomain) return null;
    
    return (
      <div 
        className="text-sm mt-1"
        style={isOrganizationThemed ? { color: 'var(--sidebar-text-muted, rgba(255,255,255,0.7))' } : { color: '#6B7280' }}
      >
        {branding.subdomain}.Tasklina.com
      </div>
    );
  };

  const shouldRenderText = showName || (variant === 'hero' && branding.subdomain);

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {renderLogo()}
      {shouldRenderText && (
        <div className="flex-1 min-w-0">
          {renderName()}
          {renderSubdomain()}
        </div>
      )}
      {isOrganizationThemed && variant === 'hero' && (
        <div className="flex items-center space-x-1 text-xs text-gray-400">
          <FiGrid className="w-3 h-3" />
          <span>Powered by Tasklina</span>
        </div>
      )}
    </div>
  );
};

export default OrganizationBrand;